import {
  ActionIcon,
  type BoxProps,
  Menu,
  type MenuProps,
  type ActionIconProps,
} from '@mantine/core'
import { useRouter } from 'next/router'
import { useEffect } from 'react'

import { useQueryGetUserLogged } from '@flecto/core/shared'
import { useLanguageState } from '@flecto/translations'
import { type CoreLocale } from '@flecto/types'

export type DropdownLanguageSelectorProps = BoxProps & {
  size?: ActionIconProps['size']
  position?: MenuProps['position']
  withSyncUser?: boolean
}

export function DropdownLanguageSelector({
  position = 'bottom-start',
  withSyncUser = true,
  className,
  size = 'md',
}: DropdownLanguageSelectorProps) {
  const { currentLanguage, changeLanguage, supportedLanguages } =
    useLanguageState()
  const { data: user } = useQueryGetUserLogged({ enabled: withSyncUser })
  const router = useRouter()

  useEffect(() => {
    const { lang } = router.query

    if (
      lang &&
      typeof lang === 'string' &&
      supportedLanguages.some((supportedLang) => supportedLang.locale === lang)
    ) {
      changeLanguage(lang as CoreLocale, false)
    } else if (user?.locale && withSyncUser) {
      changeLanguage(user.locale, false)
    }
  }, [user, withSyncUser, router.query])

  return (
    <Menu position={position}>
      <Menu.Target>
        <ActionIcon
          variant='light'
          color='gray.5'
          data-testid='select-language-trigger'
          className={className}
          size={size}
        >
          {currentLanguage.flag}
        </ActionIcon>
      </Menu.Target>
      <Menu.Dropdown mt={-10} style={{ zIndex: 1030 }}>
        {supportedLanguages.map((language) => (
          <Menu.Item
            key={language.locale}
            w='200px'
            data-testid={`select-option-${language.locale}`}
            onClick={() => changeLanguage(language.locale, withSyncUser)}
          >
            {language.flag}&nbsp;&nbsp;{language.name}
          </Menu.Item>
        ))}
      </Menu.Dropdown>
    </Menu>
  )
}
